import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { trashError, trashService, type TrashItem } from '../../services/trash.service';
import { FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { FRAME_SM, ICON, Icon, OutlineButton, fmtAgo, toneFor, useDash } from './dashUi';

const ASPECT: Record<string, string> = { portrait: '9:16', square: '1:1', landscape: '16:9' };

const purgeWord = (iso: string) => {
    const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
    if (days <= 0) return 'Deletes today';
    return `Deletes in ${days} day${days === 1 ? '' : 's'}`;
};
const ago = (iso: string) => {
    const s = fmtAgo(iso);
    // fmtAgo gives "3 days ago" / "Yesterday" / "Last week" / "Just now", or a date past 30 days.
    return /ago$|^(Just now|Yesterday|Last week)$/.test(s) ? s.toLowerCase() : `on ${s}`;
};

/** Small preview: site screenshot, or a scaled-down video frame on its tone. */
const TrashThumb: React.FC<{ t: TrashItem }> = ({ t }) => {
    const [fw, fh] = FRAME_SM[ASPECT[t.format || ''] || '9:16'];
    const k = 0.42; // FRAME_SM is sized for a 92px-tall thumb; this one is 44px
    return (
        <div style={{ position: 'relative', flex: 'none', width: 72, height: 48, borderRadius: 6, border: '1px solid var(--hv-line)', background: t.kind === 'video' ? toneFor(t.id) : 'var(--hv-page-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {t.kind === 'website' && t.thumbnailUrl && <div role="img" aria-label={`${t.name} preview`} style={{ position: 'absolute', inset: 0, backgroundImage: `url("${t.thumbnailUrl}")`, backgroundSize: 'cover', backgroundPosition: 'top' }} />}
            {t.kind === 'website' && !t.thumbnailUrl && <Icon d={ICON.site} size={18} style={{ color: 'var(--hv-line-2)' }} />}
            {t.kind === 'video' && <div style={{ width: Math.round(fw * k), height: Math.round(fh * k), borderRadius: 3, background: 'var(--hv-card)', boxShadow: '0 6px 10px -6px var(--hv-shadow)' }} />}
        </div>
    );
};

/** Trash: soft-deleted sites and videos, restorable until the retention window ends. */
export const DashTrash: React.FC = () => {
    const dash = useDash();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [items, setItems] = useState<TrashItem[] | null>(null);
    const [retention, setRetention] = useState(30);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await trashService.list();
            setItems(r.items);
            setRetention(r.retentionDays);
            setError(null);
        } catch (e) {
            setError(trashError(e, 'Could not load the trash.'));
            setItems((cur) => cur ?? []);
        }
    }, []);
    useEffect(() => { void load(); }, [load]);

    const keyOf = (t: TrashItem) => `${t.kind}-${t.id}`;
    const drop = (t: TrashItem) => setItems((cur) => (cur || []).filter((x) => keyOf(x) !== keyOf(t)));

    const restore = async (t: TrashItem) => {
        setBusy(keyOf(t));
        try {
            await trashService.restore(t.kind, t.id);
            drop(t);
            await dash.refreshProjects();
            dash.notify('success', `Restored “${t.name}”${t.wasLive ? '. The site is live again.' : ''}`);
        } catch (e) {
            dash.notify('error', trashError(e, `Could not restore “${t.name}”.`));
        } finally {
            setBusy(null);
        }
    };

    const purge = async (t: TrashItem) => {
        const ok = await confirm({
            tone: 'danger',
            title: `Delete “${t.name}” forever?`,
            description: t.kind === 'website'
                ? 'The site, its files and its history are removed for good. This cannot be undone.'
                : 'The video and its renders are removed for good. This cannot be undone.',
            confirmLabel: 'Delete forever',
            onConfirm: async () => { await trashService.purge(t.kind, t.id).catch((e) => { throw new Error(trashError(e, 'Could not delete it. Try again.')); }); },
        });
        if (!ok) return;
        drop(t);
        dash.notify('success', `Deleted “${t.name}” for good`);
    };

    return (
        <section data-screen-label="Trash" style={{ display: 'flex', flexDirection: 'column', gap: 32, animation: 'hvRise 0.4s cubic-bezier(0.22,0.9,0.24,1) both' }}>
            <div>
                <button type="button" onClick={() => navigate('/dashboard/projects')} className="hv-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, marginBottom: 10, padding: 0, border: 0, background: 'transparent', color: 'var(--hv-brand-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', textUnderlineOffset: 4 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H6M11 6l-6 6 6 6" /></svg>
                    Projects
                </button>
                <h1 style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1, letterSpacing: '-0.008em', wordSpacing: '0.06em' }}>Trash</h1>
                <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--hv-body)' }}>
                    Projects here are deleted for good after {retention} days. Live sites go offline when trashed.
                </p>
            </div>

            {error && (
                <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, background: 'var(--hv-danger-bg)', color: 'var(--hv-danger)', fontSize: 13 }}>
                    <span style={{ flex: 1 }}>{error}</span>
                    <OutlineButton onClick={() => void load()} style={{ height: 30 }}>Try again</OutlineButton>
                </div>
            )}

            {items === null ? (
                <div style={{ padding: '24px 0', fontFamily: FONT_SANS, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING…</div>
            ) : items.length > 0 ? (
                <div style={{ borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', overflow: 'hidden' }}>
                    {items.map((t, i) => {
                        const k = keyOf(t);
                        const isBusy = busy === k;
                        return (
                            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderBottom: i < items.length - 1 ? '1px solid var(--hv-line)' : 0, flexWrap: 'wrap' }}>
                                <TrashThumb t={t} />
                                <div style={{ flex: 1, minWidth: 160 }}>
                                    <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                                    <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, color: 'var(--hv-muted)' }}>
                                        <Icon d={t.kind === 'website' ? ICON.site : ICON.video} size={13} sw={2} style={{ flex: 'none' }} />
                                        <span>{t.kind === 'website' ? 'Website' : 'Video'}{t.wasLive ? ' (was live)' : ''} · deleted {ago(t.deletedAt)}</span>
                                        <span style={{ flex: 'none', width: 3, height: 3, borderRadius: 999, background: 'var(--hv-line-2)' }} />
                                        <span style={{ color: 'var(--hv-ink)', fontWeight: 500 }}>{purgeWord(t.purgeAt)}</span>
                                    </div>
                                </div>
                                <button type="button" className="hv-trash-restore" disabled={isBusy} onClick={() => void restore(t)} style={{ height: 32, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 12.5, fontWeight: 700, cursor: isBusy ? 'default' : 'pointer', opacity: isBusy ? 0.6 : 1 }}>
                                    {isBusy ? 'Restoring…' : 'Restore'}
                                </button>
                                <button type="button" className="hv-trash-del" disabled={isBusy} onClick={() => void purge(t)} style={{ height: 32, padding: '0 14px', borderRadius: 999, border: '1px solid transparent', background: 'transparent', color: 'var(--hv-danger)', fontSize: 12.5, fontWeight: 700, cursor: isBusy ? 'default' : 'pointer', opacity: isBusy ? 0.6 : 1 }}>
                                    Delete forever
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : !error ? (
                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '0.01em', wordSpacing: '0.06em' }}>Trash is empty</div>
                    <div style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hv-body)' }}>Anything you delete shows up here first.</div>
                </div>
            ) : null}
            <style>{'.hv-dash .hv-trash-restore:hover:not(:disabled){background:var(--hv-mint)!important;color:var(--hv-mint-ink)!important;border-color:var(--hv-mint)!important}.hv-dash .hv-trash-del:hover:not(:disabled){background:var(--hv-danger-bg)!important}'}</style>
        </section>
    );
};

export default DashTrash;
